import React from 'react';
import { INITIAL_AIRSTRIPS } from '../../../constants';
import { CommunicationAgentChannelSettings } from '../../../types';
import {
  formatWhatsAppBubbleTime,
  formatWhatsAppDateSeparator,
  formatWhatsAppRailTimestamp,
} from './whatsAppTime';
import type { ClientReference } from './whatsAppPanelTypes';
import {
  ComposerAttachment,
  DraftAnalysis,
  DraftFinding,
  WhatsAppLinkPreviewData,
  ThreadPlan,
  ThreadRequestItem,
  ThreadRequestEvidence,
  TimelineEvent,
  TravelContext,
  TravelPriceHint,
} from './whatsAppPanelTypes';

export const MAX_ATTACHMENT_COUNT = 4;
export const MAX_ATTACHMENT_SIZE_BYTES = 16 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'zip']);
const UNSUPPORTED_WHATSAPP_IMAGE_EXTENSIONS = new Set(['heic', 'heif']);
export const AI_DRAWER_MIN_WIDTH = 260;
export const AI_DRAWER_MAX_WIDTH = 440;
const SEVERE_HOSTILE_PATTERN = /\b(shut\s*up|idiot|stupid|dumb|useless|nonsense|fuck|f\*+k|bitch|asshole|moron|go\s+to\s+hell)\b/i;
const MONTH_INDEX: Record<string, number> = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};
const FLIGHT_INTENT_PATTERN = /\b(flight|book|booking|travel|trip|charter|airstrip|airport|pax|passenger|from\s+.+\s+to\s+.+)\b/i;

export const actionChipClass =
  'inline-flex items-center gap-2 rounded-full border border-slate-300/80 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] px-4 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_10px_20px_rgba(15,23,42,0.08)] transition hover:-translate-y-[1px] hover:border-slate-400 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.98),0_14px_24px_rgba(15,23,42,0.12)] disabled:opacity-50';

export const actionTertiaryClass =
  'inline-flex items-center gap-2 rounded-full border border-slate-200/80 bg-[linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)] px-3.5 py-2 text-[11px] font-black uppercase tracking-[0.12em] text-slate-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_8px_16px_rgba(15,23,42,0.05)] transition hover:-translate-y-[1px] hover:border-slate-300 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_12px_20px_rgba(15,23,42,0.08)] disabled:opacity-50';

export const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
export const normalizeWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();
const NORMALIZED_AIRSTRIPS = INITIAL_AIRSTRIPS.map(airstrip => ({
  code: airstrip.code,
  name: airstrip.name,
  normalized: normalizeWhitespace(airstrip.name).toLowerCase().replace(/[^a-z0-9]/g, ''),
}));

export const isUnsupportedWhatsAppImage = (file: Pick<File, 'name' | 'type'>): boolean => {
  const mimeType = String(file.type || '').toLowerCase();
  const extension = String(file.name.split('.').pop() || '').toLowerCase();
  return (
    mimeType.includes('heic') ||
    mimeType.includes('heif') ||
    UNSUPPORTED_WHATSAPP_IMAGE_EXTENSIONS.has(extension)
  );
};

export const allowedAttachment = (file: File): boolean => {
  const mimeType = String(file.type || '').toLowerCase();
  if (mimeType.startsWith('image/') || mimeType.startsWith('video/') || mimeType.startsWith('audio/')) return true;
  const extension = String(file.name.split('.').pop() || '').toLowerCase();
  return ALLOWED_ATTACHMENT_EXTENSIONS.has(extension) || UNSUPPORTED_WHATSAPP_IMAGE_EXTENSIONS.has(extension);
};

export const fileToAttachment = async (file: File): Promise<ComposerAttachment> => {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
  const base64Data = String(dataUrl.split(',')[1] || '');
  return {
    id: `${file.name}-${file.size}-${file.lastModified}`,
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    fileSizeBytes: file.size,
    base64Data,
    previewUrl: file.type.startsWith('image/') || file.type.startsWith('video/') ? dataUrl : null,
  };
};

export const messageTimestamp = (value?: string | null): string => {
  if (!value) return '';
  return formatWhatsAppBubbleTime(value);
};

export const railTimestampLabel = (value?: string | null): string => {
  return formatWhatsAppRailTimestamp(value);
};

const normalizePhoneDigits = (value?: string | null): string => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  if (digits.length <= 12) return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  return `+${digits}`;
};

type WhatsAppIdentityValue = {
  jid?: string | null;
  phone?: string | null;
  displayName?: string | null;
  displayPhone?: string | null;
  kind?: string | null;
};

type WhatsAppIdentityContext = {
  participantMap?: Record<string, WhatsAppIdentityValue> | null;
  displayName?: string | null;
  displayPhone?: string | null;
  identityKind?: string | null;
  customerName?: string | null;
  maosCustomerName?: string | null;
  customerPhone?: string | null;
  customerJid?: string | null;
  senderName?: string | null;
  senderJid?: string | null;
  groupParticipantJid?: string | null;
  groupName?: string | null;
  chatType?: string | null;
};

const isRawInternalHandle = (value?: string | null): boolean => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return false;
  if (/@(lid|s\.whatsapp\.net|g\.us|newsletter)$/.test(raw)) return true;
  if (/^\d{12,}$/.test(raw)) return true;
  return false;
};

const isHumanDisplayLabel = (value?: string | null): boolean => {
  const label = String(value || '').trim();
  if (!label) return false;
  if (label.startsWith('+')) return false;
  if (/^\d[\d\s]+$/.test(label)) return false;
  if (/@(lid|s\.whatsapp\.net|g\.us|newsletter)$/i.test(label)) return false;
  return /[A-Za-z\u00C0-\u024F]/.test(label);
};

export const formatWhatsappJidForOperator = (jid?: string | null): string => {
  const raw = String(jid || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower.endsWith('@lid')) return '';
  if (lower.endsWith('@g.us')) return '';
  if (lower.endsWith('@s.whatsapp.net')) {
    const phone = normalizePhoneDigits(raw.split('@')[0]);
    return phone || '';
  }
  return '';
};

const buildParticipantLookup = (context?: WhatsAppIdentityContext | null): Map<string, WhatsAppIdentityValue> => {
  const lookup = new Map<string, WhatsAppIdentityValue>();
  const add = (key?: string | null, value?: WhatsAppIdentityValue | null) => {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey || !value) return;
    lookup.set(normalizedKey, value);
  };
  const map = context?.participantMap && typeof context.participantMap === 'object' ? context.participantMap : null;
  if (map) {
    Object.entries(map).forEach(([key, value]) => {
      if (!value || typeof value !== 'object') return;
      add(key, value);
      const digits = normalizePhoneDigits(key.replace(/^@/, ''));
      if (digits) add(digits, value);
    });
  }
  const baseDisplayName =
    String(context?.displayName || '').trim() ||
    String(context?.maosCustomerName || '').trim() ||
    String(context?.customerName || '').trim() ||
    String(context?.senderName || '').trim();
  const baseDisplayPhone =
    String(context?.displayPhone || '').trim() ||
    normalizePhoneDigits(context?.customerPhone || context?.customerJid || context?.senderJid || '');
  const baseValue: WhatsAppIdentityValue | null =
    baseDisplayName || baseDisplayPhone
      ? {
          jid: String(context?.customerJid || context?.senderJid || '').trim() || null,
          phone: baseDisplayPhone || null,
          displayName: baseDisplayName || null,
          displayPhone: baseDisplayPhone || null,
          kind: String(context?.identityKind || '').trim() || 'CONTACT',
        }
      : null;
  if (baseValue) {
    add(context?.customerJid, baseValue);
    add(context?.senderJid, baseValue);
    add(context?.groupParticipantJid, baseValue);
    const customerDigits = normalizePhoneDigits(context?.customerPhone || context?.customerJid || '');
    if (customerDigits) {
      add(customerDigits, baseValue);
      add(`@${customerDigits}`, baseValue);
    }
    const senderDigits = normalizePhoneDigits(context?.senderJid || context?.groupParticipantJid || '');
    if (senderDigits) {
      add(senderDigits, baseValue);
      add(`@${senderDigits}`, baseValue);
    }
  }
  return lookup;
};

export const resolveWhatsAppIdentity = (
  jidOrMention?: string | null,
  context?: WhatsAppIdentityContext | null,
): {
  displayName: string;
  displayPhone: string | null;
  kind: 'CONTACT' | 'GROUP' | 'CHANNEL' | 'GROUP_MEMBER' | 'UNKNOWN';
} => {
  const raw = String(jidOrMention || '').trim();
  const lower = raw.toLowerCase();
  const lookup = buildParticipantLookup(context);
  const digits = normalizePhoneDigits(raw.replace(/^@/, '').split('@')[0] || raw);
  const isInlineMention = raw.startsWith('@');
  const isGroupContext =
    String(context?.chatType || '').trim().toUpperCase() === 'GROUP' ||
    String(context?.identityKind || '').trim().toUpperCase() === 'GROUP';
  const inlineFallback = isGroupContext ? '@Unknown member' : '@contact';
  const direct = lookup.get(raw) || lookup.get(lower) || lookup.get(digits) || lookup.get(`@${digits}`);
  if (direct) {
    const resolvedName = String(direct.displayName || '').trim();
    const resolvedPhone = String(direct.displayPhone || '').trim() || normalizePhoneDigits(direct.phone || direct.jid || '');
    const hasHumanResolvedName = isHumanDisplayLabel(resolvedName);
    return {
      displayName: isInlineMention
        ? (hasHumanResolvedName
            ? `@${resolvedName.replace(/^@/, '')}`
            : inlineFallback)
        : resolvedName || resolvedPhone || 'WhatsApp contact',
      displayPhone: resolvedPhone || null,
      kind: (String(direct.kind || '').trim().toUpperCase() as any) || 'CONTACT',
    };
  }
  if (lower.endsWith('@newsletter')) {
    return { displayName: isInlineMention ? '@WhatsApp channel' : 'WhatsApp channel', displayPhone: null, kind: 'CHANNEL' };
  }
  if (lower.endsWith('@g.us')) {
    return { displayName: isInlineMention ? '@WhatsApp group' : 'WhatsApp group', displayPhone: null, kind: 'GROUP' };
  }
  if (lower.endsWith('@lid')) {
    return { displayName: isInlineMention ? inlineFallback : 'Unknown member', displayPhone: null, kind: 'UNKNOWN' };
  }
  const displayPhone = digits ? normalizePhoneDigits(digits) : '';
  if (lower.endsWith('@s.whatsapp.net')) {
    return {
      displayName: isInlineMention ? inlineFallback : displayPhone || 'WhatsApp contact',
      displayPhone: displayPhone || null,
      kind: 'CONTACT',
    };
  }
  if (isInlineMention && digits) {
    return {
      displayName: inlineFallback,
      displayPhone: null,
      kind: isGroupContext ? 'GROUP_MEMBER' : 'CONTACT',
    };
  }
  if (digits) {
    return {
      displayName: displayPhone || 'WhatsApp contact',
      displayPhone: displayPhone || null,
      kind: 'CONTACT',
    };
  }
  const fallbackKind = String(context?.identityKind || '').trim().toUpperCase();
  if (fallbackKind === 'GROUP') return { displayName: String(context?.groupName || '').trim() || 'WhatsApp group', displayPhone: null, kind: 'GROUP' };
  if (fallbackKind === 'CHANNEL') return { displayName: 'WhatsApp channel', displayPhone: null, kind: 'CHANNEL' };
  return { displayName: 'WhatsApp contact', displayPhone: null, kind: 'CONTACT' };
};

export const sanitizeWhatsAppTextForDisplay = (text?: string | null, context?: WhatsAppIdentityContext | null): string => {
  const source = String(text || '').trim();
  if (!source) return '';
  const isGroupContext =
    String(context?.chatType || '').trim().toUpperCase() === 'GROUP' ||
    String(context?.identityKind || '').trim().toUpperCase() === 'GROUP';
  const unresolvedInlineFallback = isGroupContext ? '@Unknown member' : '@contact';
  return source
    .replace(/@\+\d[\d\s]{6,}\b/g, unresolvedInlineFallback)
    .replace(/@([0-9]{6,})@lid\b/gi, (full) => {
      const resolved = resolveWhatsAppIdentity(full, context);
      return `@${resolved.displayName.replace(/^@/, '')}`;
    })
    .replace(/@([0-9]{6,})@s\.whatsapp\.net\b/gi, (full) => {
      const resolved = resolveWhatsAppIdentity(full, context);
      return `@${resolved.displayName.replace(/^@/, '')}`;
    })
    .replace(/@([0-9]{6,})@newsletter\b/gi, () => '@WhatsApp channel')
    .replace(/@([0-9]{6,})@g\.us\b/gi, () => '@WhatsApp group')
    .replace(/@(\d{6,})\b/gi, (full) => {
      const resolved = resolveWhatsAppIdentity(full, context);
      return `@${String(resolved.displayName || 'Unknown member').replace(/^@/, '')}`;
    })
    .replace(/\b([0-9]{6,})@lid\b/gi, () => 'Unknown member')
    .replace(/\b([0-9]{6,})@s\.whatsapp\.net\b/gi, (full) => resolveWhatsAppIdentity(full, context).displayName)
    .replace(/\b([0-9]{6,})@g\.us\b/gi, 'WhatsApp group')
    .replace(/\b([0-9]{6,})@newsletter\b/gi, 'WhatsApp channel')
    .replace(/\s+/g, ' ')
    .trim();
};

export const sanitizeWhatsappPreviewText = (text?: string | null, context?: WhatsAppIdentityContext | null): string =>
  sanitizeWhatsAppTextForDisplay(text, context);

type RenderWhatsAppTextOptions = {
  highlightTerm?: string | null;
  linkClassName?: string;
  highlightClassName?: string;
  stopPropagationOnLinks?: boolean;
};

const WHATSAPP_URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;
const DEFAULT_WHATSAPP_LINK_CLASS =
  'cursor-pointer break-all text-inherit underline decoration-current underline-offset-2 transition-opacity hover:opacity-80';
const DEFAULT_WHATSAPP_HIGHLIGHT_CLASS = 'rounded bg-amber-200/80 px-0.5 text-amber-950';

const countChar = (value: string, target: string) => {
  let count = 0;
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] === target) count += 1;
  }
  return count;
};

const trimUrlCandidate = (value: string): { href: string; trailing: string } => {
  let href = String(value || '');
  let trailing = '';
  while (href) {
    const last = href[href.length - 1];
    if (/[.,!?;:]/.test(last)) {
      trailing = `${last}${trailing}`;
      href = href.slice(0, -1);
      continue;
    }
    if (last === ')') {
      const opens = countChar(href, '(');
      const closes = countChar(href, ')');
      if (closes > opens) {
        trailing = `)${trailing}`;
        href = href.slice(0, -1);
        continue;
      }
    }
    break;
  }
  return { href, trailing };
};

const isSafeHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const renderHighlightedText = (
  text: string,
  options: RenderWhatsAppTextOptions,
  keyPrefix: string,
): React.ReactNode[] => {
  const raw = String(text || '');
  const term = String(options.highlightTerm || '').trim();
  if (!raw) return [];
  if (term.length < 2) return [raw];
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'ig');
  const parts = raw.split(regex);
  return parts.map((part, index) => {
    if (!part) return null;
    if (part.toLowerCase() === term.toLowerCase()) {
      return React.createElement(
        'mark',
        {
          key: `${keyPrefix}-mark-${index}`,
          className: options.highlightClassName || DEFAULT_WHATSAPP_HIGHLIGHT_CLASS,
        },
        part,
      );
    }
    return React.createElement(React.Fragment, { key: `${keyPrefix}-text-${index}` }, part);
  });
};

const renderUnformattedTextWithLinks = (
  text: string,
  options: RenderWhatsAppTextOptions,
  keyPrefix: string,
): React.ReactNode[] => {
  const source = String(text || '');
  if (!source) return [];
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let matchIndex = 0;
  WHATSAPP_URL_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = WHATSAPP_URL_PATTERN.exec(source))) {
    const rawUrl = String(match[0] || '');
    const start = match.index;
    if (start > lastIndex) {
      nodes.push(
        ...renderHighlightedText(
          source.slice(lastIndex, start),
          options,
          `${keyPrefix}-lead-${matchIndex}`,
        ),
      );
    }
    const { href, trailing } = trimUrlCandidate(rawUrl);
    if (isSafeHttpUrl(href)) {
      nodes.push(
        React.createElement(
          'a',
          {
            key: `${keyPrefix}-url-${matchIndex}`,
            href,
            target: '_blank',
            rel: 'noopener noreferrer',
            className: options.linkClassName || DEFAULT_WHATSAPP_LINK_CLASS,
            onClick: options.stopPropagationOnLinks
              ? (event: React.MouseEvent<HTMLAnchorElement>) => event.stopPropagation()
              : undefined,
          },
          href,
        ),
      );
    } else {
      nodes.push(
        ...renderHighlightedText(rawUrl, options, `${keyPrefix}-unsafe-${matchIndex}`),
      );
    }
    if (trailing) {
      nodes.push(
        ...renderHighlightedText(
          trailing,
          options,
          `${keyPrefix}-trail-${matchIndex}`,
        ),
      );
    }
    lastIndex = start + rawUrl.length;
    matchIndex += 1;
  }
  if (lastIndex < source.length) {
    nodes.push(
      ...renderHighlightedText(
        source.slice(lastIndex),
        options,
        `${keyPrefix}-tail`,
      ),
    );
  }
  return nodes;
};

const renderFormattedWhatsAppSegment = (
  segment: string,
  options: RenderWhatsAppTextOptions,
  keyPrefix: string,
): React.ReactNode => {
  const source = String(segment || '');
  if (!source) return null;
  const wrap = (tag: 'strong' | 'em' | 'span' | 'code', content: string, extraClassName?: string) =>
    React.createElement(
      tag,
      {
        key: keyPrefix,
        className: extraClassName,
      },
      ...renderUnformattedTextWithLinks(content, options, `${keyPrefix}-inner`),
    );
  if (source.startsWith('*') && source.endsWith('*') && source.length > 2) {
    return wrap('strong', source.slice(1, -1));
  }
  if (source.startsWith('_') && source.endsWith('_') && source.length > 2) {
    return wrap('em', source.slice(1, -1));
  }
  if (source.startsWith('~') && source.endsWith('~') && source.length > 2) {
    return wrap('span', source.slice(1, -1), 'line-through');
  }
  if (source.startsWith('`') && source.endsWith('`') && source.length > 2) {
    return wrap(
      'code',
      source.slice(1, -1),
      'rounded-[3px] bg-[rgba(150,150,150,0.15)] px-[4px] py-[2px] text-[13px] font-mono',
    );
  }
  return React.createElement(
    React.Fragment,
    { key: keyPrefix },
    ...renderUnformattedTextWithLinks(source, options, `${keyPrefix}-plain`),
  );
};

export const renderWhatsAppTextWithLinks = (
  text?: string | null,
  options: RenderWhatsAppTextOptions = {},
): React.ReactNode[] => {
  const source = String(text || '');
  if (!source) return [];
  const segments = source.split(/(`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g);
  return segments.map((segment, index) =>
    renderFormattedWhatsAppSegment(segment, options, `wa-text-${index}-${segment.slice(0, 8)}`),
  );
};

const extractCompactExtendedTextMessage = (rawPayload: any): any | null => {
  if (!rawPayload || typeof rawPayload !== 'object') return null;
  const source = rawPayload as any;
  return (
    source?.message?.message?.extendedTextMessage ||
    source?.sendResult?.message?.extendedTextMessage ||
    source?.extendedTextMessage ||
    null
  );
};

const toBase64FromByteObject = (value: Record<string, unknown>): string | null => {
  const orderedValues = Object.keys(value)
    .filter(key => /^\d+$/.test(key))
    .sort((left, right) => Number(left) - Number(right))
    .map(key => Number(value[key]))
    .filter(item => Number.isFinite(item) && item >= 0 && item <= 255);
  if (!orderedValues.length || typeof globalThis.btoa !== 'function') return null;
  let binary = '';
  orderedValues.forEach(item => {
    binary += String.fromCharCode(item);
  });
  try {
    return globalThis.btoa(binary);
  } catch {
    return null;
  }
};

const toThumbnailDataUrl = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('data:image/')) return trimmed;
    return `data:image/jpeg;base64,${trimmed}`;
  }
  if (typeof value === 'object') {
    const base64 = toBase64FromByteObject(value as Record<string, unknown>);
    return base64 ? `data:image/jpeg;base64,${base64}` : null;
  }
  return null;
};

export const extractWhatsAppLinkPreview = (rawPayload?: any): WhatsAppLinkPreviewData | null => {
  const extended = extractCompactExtendedTextMessage(rawPayload);
  if (!extended || typeof extended !== 'object') return null;
  const rawUrl =
    String(extended.canonicalUrl || extended.matchedText || extended.previewUrl || '').trim() || null;
  const safeUrl = rawUrl && isSafeHttpUrl(rawUrl) ? rawUrl : null;
  if (!safeUrl) return null;
  const rawThumbnailUrl =
    String(extended.thumbnailUrl || extended.previewImageUrl || extended.image || '').trim() || null;
  const safeThumbnailUrl = rawThumbnailUrl && isSafeHttpUrl(rawThumbnailUrl) ? rawThumbnailUrl : null;
  let domain = '';
  try {
    domain = new URL(safeUrl).hostname.replace(/^www\./i, '');
  } catch {
    domain = '';
  }
  return {
    url: safeUrl,
    canonicalUrl: String(extended.canonicalUrl || '').trim() || null,
    matchedText: String(extended.matchedText || '').trim() || null,
    title: String(extended.title || '').trim() || null,
    description: String(extended.description || '').trim() || null,
    domain: domain || 'Link preview',
    thumbnailDataUrl: toThumbnailDataUrl(extended.jpegThumbnail),
    thumbnailUrl: safeThumbnailUrl,
  };
};

const clipTextCenter = (text: string, centerIndex: number, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  const room = Math.max(12, maxLength - 6);
  const half = Math.floor(room / 2);
  let start = Math.max(0, centerIndex - half);
  let end = Math.min(text.length, start + room);
  if (end >= text.length) {
    end = text.length;
    start = Math.max(0, end - room);
  }
  const slice = text.slice(start, end).trim();
  const prefix = start > 0 ? '... ' : '';
  const suffix = end < text.length ? ' ...' : '';
  return `${prefix}${slice}${suffix}`.trim();
};

export const buildReadableSearchSnippet = (args: {
  snippet?: string | null;
  mediaFileName?: string | null;
  messageType?: string | null;
  searchTerm?: string | null;
  maxLength?: number;
  context?: WhatsAppIdentityContext | null;
}): string => {
  const source = sanitizeWhatsAppTextForDisplay(
    args.snippet || args.mediaFileName || args.messageType || 'Matched message',
    args.context,
  );
  const normalized = normalizeWhitespace(source);
  if (!normalized) return 'Matched message';
  const maxLength = Math.max(80, Number(args.maxLength || 180));
  const term = normalizeWhitespace(String(args.searchTerm || ''));
  if (normalized.length <= maxLength) return normalized;
  if (term.length >= 2) {
    const lowerText = normalized.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const matchIndex = lowerText.indexOf(lowerTerm);
    if (matchIndex >= 0) {
      return clipTextCenter(normalized, matchIndex + Math.floor(lowerTerm.length / 2), maxLength);
    }
  }
  const clipped = normalized.slice(0, Math.max(0, maxLength - 4)).trimEnd();
  return `${clipped} ...`;
};

export const formatWhatsAppMediaLabel = (kind?: string | null): string => {
  const value = String(kind || '').trim().toUpperCase();
  if (value === 'IMAGE') return 'Photo';
  if (value === 'VIDEO') return 'Video';
  if (value === 'AUDIO') return 'Voice note';
  if (value === 'DOCUMENT') return 'Document';
  return 'Media';
};

export const buildDistinctSearchMetaChips = (chips: Array<string | null | undefined>): string[] => {
  const seen = new Set<string>();
  const results: string[] = [];
  chips.forEach((value) => {
    const next = sanitizeWhatsappPreviewText(String(value || '')).replace(/\s+/g, ' ').trim();
    if (!next) return;
    if (isRawInternalHandle(next)) return;
    const key = next.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    results.push(next);
  });
  return results;
};

export const formatWhatsAppDisplayName = (item?: {
  displayName?: string | null;
  displayPhone?: string | null;
  identityKind?: string | null;
  participantMap?: Record<string, WhatsAppIdentityValue> | null;
  chatType?: string | null;
  groupName?: string | null;
  customerName?: string | null;
  contactName?: string | null;
  displayName?: string | null;
  maosCustomerName?: string | null;
  customerPhone?: string | null;
  phone?: string | null;
  customerJid?: string | null;
  senderName?: string | null;
  pushName?: string | null;
} | null): string => {
  const enriched = String(item?.displayName || '').trim();
  if (enriched && !isRawInternalHandle(enriched)) return enriched;
  const chatType = String(item?.chatType || '').toUpperCase();
  const groupName = String(item?.groupName || '').trim();
  if (chatType === 'GROUP') return groupName || 'WhatsApp group';
  const isNewsletter = String(item?.customerJid || '').trim().toLowerCase().endsWith('@newsletter');
  if (isNewsletter) return groupName || 'WhatsApp channel';
  const directName = [
    item?.displayName,
    item?.contactName,
    item?.maosCustomerName,
    item?.customerName,
    item?.senderName,
    item?.pushName,
  ]
    .map(value => String(value || '').trim())
    .find(Boolean);
  if (directName) {
    if (!isRawInternalHandle(directName)) return directName;
  }
  const phone = normalizePhoneDigits(item?.customerPhone || item?.phone);
  if (phone) return phone;
  const jidPhone = formatWhatsappJidForOperator(item?.customerJid);
  if (jidPhone) return jidPhone;
  return 'WhatsApp contact';
};

export const messageDayKey = (value?: string | null): string => {
  if (!value) return 'unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'unknown' : date.toDateString();
};

export const formatDayLabel = (value?: string | null): string => {
  return formatWhatsAppDateSeparator(value);
};

export const filterChipClass = (active: boolean, tone: 'slate' | 'emerald' | 'amber' | 'indigo' | 'sky' = 'slate') =>
  `inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-[0.12em] transition ${
    active
      ? tone === 'emerald'
        ? 'bg-emerald-600 text-white'
        : tone === 'amber'
          ? 'bg-amber-500 text-white'
          : tone === 'indigo'
            ? 'bg-indigo-600 text-white'
            : tone === 'sky'
              ? 'bg-sky-600 text-white'
              : 'bg-slate-900 text-white'
      : tone === 'emerald'
        ? 'border border-emerald-200 bg-white text-emerald-700'
        : tone === 'amber'
          ? 'border border-amber-200 bg-white text-amber-700'
          : tone === 'indigo'
            ? 'border border-indigo-200 bg-white text-indigo-700'
            : tone === 'sky'
              ? 'border border-sky-200 bg-white text-sky-700'
              : 'border border-slate-300 bg-white text-slate-700'
  }`;

export const quotedTypeLabel = (value?: string | null): string => {
  const key = String(value || '').toUpperCase();
  if (key === 'IMAGE') return 'Image';
  if (key === 'VIDEO') return 'Video';
  if (key === 'AUDIO') return 'Audio';
  if (key === 'DOCUMENT') return 'Document';
  return 'Message';
};

export const extractConversationReferences = (timelineEvents: TimelineEvent[]): string[] => {
  const matches = new Set<string>();
  const pattern = /\b(?:estimate|quote|booking|ref(?:erence)?|package)\s*#?\s*([a-z0-9-]{3,})\b/gi;
  timelineEvents.forEach(event => {
    const text = `${event.body || ''} ${event.quotedPreview || ''}`;
    for (const match of text.matchAll(pattern)) {
      matches.add(normalizeWhitespace(match[0]));
    }
  });
  return Array.from(matches).slice(0, 6);
};

export const titleCaseSeverity = (severity: DraftFinding['severity']): string =>
  severity === 'HIGH' ? 'High' : severity === 'MEDIUM' ? 'Medium' : 'Low';

const getEventReadableText = (event?: TimelineEvent | null): string =>
  normalizeWhitespace(
    `${event?.body || ''} ${event?.quotedPreview || ''} ${event?.transcriptText || ''} ${event?.mediaFileName || ''}`
  );

const getLastCustomerMessage = (timelineEvents: TimelineEvent[]): TimelineEvent | null =>
  [...timelineEvents].reverse().find(event => event.kind === 'INBOUND' && !!getEventReadableText(event)) || null;

const getFocusedCustomerMessage = (
  timelineEvents: TimelineEvent[],
  replyTargetEvent?: TimelineEvent | null,
): TimelineEvent | null => {
  if (replyTargetEvent?.kind === 'INBOUND' && getEventReadableText(replyTargetEvent)) return replyTargetEvent;
  if (!replyTargetEvent?.at) return getLastCustomerMessage(timelineEvents);
  const targetTime = new Date(replyTargetEvent.at).getTime();
  return [...timelineEvents]
    .reverse()
    .find(event => event.kind === 'INBOUND' && new Date(event.at).getTime() <= targetTime && !!getEventReadableText(event)) || null;
};

const getLastOutboundMessage = (timelineEvents: TimelineEvent[]): TimelineEvent | null =>
  [...timelineEvents].reverse().find(event => event.kind === 'OUTBOUND' && !!getEventReadableText(event)) || null;

const getRecentHostileOutbound = (timelineEvents: TimelineEvent[]): TimelineEvent | null =>
  [...timelineEvents]
    .reverse()
    .find(event => event.kind === 'OUTBOUND' && SEVERE_HOSTILE_PATTERN.test(normalizeWhitespace(event.body || ''))) || null;

const extractRouteHint = (value: string): string | null => {
  const match = value.match(/\bfrom\s+([a-z\s]+?)\s+to\s+([a-z\s]+?)(?:[.!?,]|$)/i);
  if (!match) return null;
  const from = normalizeWhitespace(match[1]);
  const to = normalizeWhitespace(match[2]);
  if (!from || !to) return null;
  return `${from} to ${to}`;
};

const normalizeLocationToken = (value: string): string => normalizeWhitespace(value).toLowerCase().replace(/[^a-z0-9]/g, '');
const canonicalizeAirstripCode = (value?: string | null): string => {
  const code = String(value || '').trim().toUpperCase();
  if (!code) return '';
  if (code === 'ARK' || code === 'HTAR' || code === 'ARUSHA') return 'HTAR';
  return code;
};

const levenshteinDistance = (left: string, right: string): number => {
  const a = left;
  const b = right;
  const matrix = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) matrix[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }
  return matrix[a.length][b.length];
};

const resolveAirstrip = (value?: string | null): { code: string; label: string } | null => {
  const normalized = normalizeLocationToken(String(value || ''));
  if (!normalized) return null;
  const direct = NORMALIZED_AIRSTRIPS.find(item =>
    item.normalized === normalized ||
    item.code.toLowerCase() === normalized ||
    item.normalized.includes(normalized) ||
    normalized.includes(item.normalized),
  );
  if (direct) {
    return {
      code: canonicalizeAirstripCode(direct.code),
      label: direct.name,
    };
  }
  const fuzzy = NORMALIZED_AIRSTRIPS
    .map(item => ({
      ...item,
      distance: levenshteinDistance(item.normalized, normalized),
    }))
    .filter(item => item.distance <= 2)
    .sort((left, right) => left.distance - right.distance)[0];
  if (!fuzzy) return null;
  return {
    code: canonicalizeAirstripCode(fuzzy.code),
    label: fuzzy.name,
  };
};

const extractPassengerCount = (value: string): number | null => {
  const patterns = [
    /\bfor\s+(\d{1,2})\s*(?:pax|passengers|people)\b/i,
    /\b(\d{1,2})\s*(?:pax|passengers|people)\b/i,
    /\bpassengers?\s*(?:for|:)?\s*(\d{1,2})\b/i,
  ];
  for (const pattern of patterns) {
    const match = value.match(pattern);
    if (!match) continue;
    const count = Number(match[1]);
    if (Number.isFinite(count) && count > 0) return count;
  }
  return null;
};

const extractTravelDate = (value: string): { label: string; isoDate: string | null } | null => {
  const monthMatch = value.match(/\b(\d{1,2})(?:st|nd|rd|th)?(?:\s+of)?\s+(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t|tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/i);
  if (monthMatch) {
    const day = Number(monthMatch[1]);
    const month = MONTH_INDEX[String(monthMatch[2] || '').toLowerCase()];
    if (Number.isFinite(day) && month !== undefined) {
      const now = new Date();
      let year = now.getUTCFullYear();
      const candidate = new Date(Date.UTC(year, month, day));
      if (candidate.getTime() < now.getTime() - 86400000 * 7) year += 1;
      const resolved = new Date(Date.UTC(year, month, day));
      return {
        label: `${day} ${resolved.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' })} ${year}`,
        isoDate: resolved.toISOString().slice(0, 10),
      };
    }
  }

  const dayOnlyMatch = value.match(/\bfor\s+(\d{1,2})(?!\s*(?:pax|passengers|people|am|pm))\b/i);
  if (dayOnlyMatch) {
    const day = Number(dayOnlyMatch[1]);
    if (Number.isFinite(day) && day >= 1 && day <= 31) {
      return {
        label: `Day ${day} (month not confirmed)`,
        isoDate: null,
      };
    }
  }
  return null;
};

const describeTravelRequest = (
  fromLabel: string | null,
  toLabel: string | null,
  dateLabel: string | null,
  passengerCount: number | null,
): string => {
  const route = fromLabel && toLabel ? `${fromLabel} -> ${toLabel}` : 'Route not fully confirmed';
  const date = dateLabel || 'date not confirmed';
  const pax = passengerCount ? `${passengerCount} pax` : 'pax not confirmed';
  return `${route}, ${date}, ${pax}`;
};

const buildEventTravelContext = (event: TimelineEvent): TravelContext => {
  const text = getEventReadableText(event);
  const routeMatch = text.match(/\bfrom\s+([a-z\s]+?)\s+to\s+([a-z\s]+?)(?:\s+for\b|[.!?,]|$)/i);
  const fromRaw = routeMatch ? normalizeWhitespace(routeMatch[1]) : '';
  const toRaw = routeMatch ? normalizeWhitespace(routeMatch[2]) : '';
  const fromResolved = resolveAirstrip(fromRaw);
  const toResolved = resolveAirstrip(toRaw);
  const passengerCount = extractPassengerCount(text);
  const travelDate = extractTravelDate(text);
  const routeText = fromRaw && toRaw ? `${fromRaw} to ${toRaw}` : null;
  const isFlightRequest = FLIGHT_INTENT_PATTERN.test(text);

  return {
    routeText,
    fromLabel: fromResolved?.label || (fromRaw || null),
    fromCode: fromResolved?.code || null,
    toLabel: toResolved?.label || (toRaw || null),
    toCode: toResolved?.code || null,
    passengerCount,
    dateLabel: travelDate?.label || null,
    isoDate: travelDate?.isoDate || null,
    isFlightRequest,
    hasCompleteRoute: !!(fromResolved?.code && toResolved?.code),
    hasEnoughDetailsForPricing: !!(fromResolved?.code && toResolved?.code && passengerCount),
  };
};

export const parseTravelContext = (timelineEvents: TimelineEvent[]): TravelContext => {
  const inboundBodies = timelineEvents
    .filter(event => event.kind === 'INBOUND')
    .map(event => getEventReadableText(event))
    .filter(Boolean);
  const text = inboundBodies.join(' | ');
  const routeMatch = text.match(/\bfrom\s+([a-z\s]+?)\s+to\s+([a-z\s]+?)(?:\s+for\b|[.!?,]|$)/i);
  const fromRaw = routeMatch ? normalizeWhitespace(routeMatch[1]) : '';
  const toRaw = routeMatch ? normalizeWhitespace(routeMatch[2]) : '';
  const fromResolved = resolveAirstrip(fromRaw);
  const toResolved = resolveAirstrip(toRaw);
  const passengerCount = extractPassengerCount(text);
  const travelDate = extractTravelDate(text);
  const routeText = fromRaw && toRaw ? `${fromRaw} to ${toRaw}` : null;
  const isFlightRequest = FLIGHT_INTENT_PATTERN.test(text);

  return {
    routeText,
    fromLabel: fromResolved?.label || (fromRaw || null),
    fromCode: fromResolved?.code || null,
    toLabel: toResolved?.label || (toRaw || null),
    toCode: toResolved?.code || null,
    passengerCount,
    dateLabel: travelDate?.label || null,
    isoDate: travelDate?.isoDate || null,
    isFlightRequest,
    hasCompleteRoute: !!(fromResolved?.code && toResolved?.code),
    hasEnoughDetailsForPricing: !!(fromResolved?.code && toResolved?.code && passengerCount),
  };
};

const determineRequestStatus = (
  requestEvent: TimelineEvent,
  requestContext: TravelContext,
  timelineEvents: TimelineEvent[],
): ThreadRequestItem['status'] => {
  const requestTime = new Date(requestEvent.at).getTime();
  const afterRequest = timelineEvents.filter(event => new Date(event.at).getTime() > requestTime);
  const hasOutboundAfter = afterRequest.some(event => event.kind === 'OUTBOUND' && !!getEventReadableText(event));
  const hasInboundAfter = afterRequest.some(event => event.kind === 'INBOUND' && !!getEventReadableText(event));

  if (!requestContext.hasCompleteRoute || !requestContext.dateLabel || !requestContext.passengerCount) {
    return hasOutboundAfter && !hasInboundAfter ? 'WAITING_CLIENT' : 'NEEDS_CLARIFICATION';
  }
  if (hasOutboundAfter && !hasInboundAfter) return 'WAITING_CLIENT';
  if (!hasOutboundAfter) return 'NEEDS_REPLY';
  return 'READY_FOR_PRICING';
};

export const buildThreadPlan = (timelineEvents: TimelineEvent[]): ThreadPlan => {
  const inboundEvents = timelineEvents.filter(event => event.kind === 'INBOUND' && !!getEventReadableText(event));
  const requestEvents = inboundEvents.filter(event => FLIGHT_INTENT_PATTERN.test(getEventReadableText(event)));
  const openRequests: ThreadRequestItem[] = requestEvents.map(event => {
    const context = buildEventTravelContext(event);
    const missingFields: string[] = [];
    if (!context.hasCompleteRoute) missingFields.push('route');
    if (!context.dateLabel) missingFields.push('travel date');
    if (!context.passengerCount) missingFields.push('passenger count');
    return {
      id: event.messageId || event.id,
      sourceMessageId: event.messageId,
      sourceAt: event.at,
      summary: describeTravelRequest(context.fromLabel, context.toLabel, context.dateLabel, context.passengerCount),
      fromLabel: context.fromLabel,
      fromCode: context.fromCode,
      toLabel: context.toLabel,
      toCode: context.toCode,
      passengerCount: context.passengerCount,
      dateLabel: context.dateLabel,
      isoDate: context.isoDate,
      status: determineRequestStatus(event, context, timelineEvents),
      missingFields,
      lastClientText: getEventReadableText(event),
    };
  });

  const latestInbound = getLastCustomerMessage(timelineEvents);
  const latestClientNeed = latestInbound ? getEventReadableText(latestInbound) : null;
  const summary = openRequests.length > 0
    ? `${openRequests.length} open request${openRequests.length === 1 ? '' : 's'} in this thread. Latest client need: ${latestClientNeed || openRequests[openRequests.length - 1]?.summary || 'Not available'}.`
    : (latestClientNeed ? `Client latest message: ${latestClientNeed}` : 'No open client requests detected in this thread.');
  const nextSteps = openRequests.length > 0
    ? openRequests.slice(0, 3).map(request => {
        if (request.status === 'NEEDS_CLARIFICATION') {
          return `Clarify ${request.missingFields.join(' and ')} for ${request.fromLabel || 'this route'}${request.toLabel ? ` -> ${request.toLabel}` : ''}.`;
        }
        if (request.status === 'NEEDS_REPLY') {
          return `Reply on ${request.fromLabel || 'the request'}${request.toLabel ? ` -> ${request.toLabel}` : ''} and move it to quote or waiting client.`;
        }
        if (request.status === 'WAITING_CLIENT') {
          return `Waiting on client confirmation for ${request.summary}.`;
        }
        return `Prepare pricing/quote for ${request.summary}.`;
      })
    : ['Read the latest client message and decide the next action.'];
  const handoverSummary = openRequests.length > 0
    ? `Thread handover: ${openRequests.length} active request${openRequests.length === 1 ? '' : 's'}. ${nextSteps[0]}`
    : 'Thread handover: no structured travel requests detected yet.';

  return {
    summary,
    handoverSummary,
    openRequests,
    latestClientNeed,
    nextSteps,
  };
};

type RequestEvidenceLink = {
  id: string;
  MediaAsset?: {
    id: string;
    evidenceType?: string | null;
    fileName?: string | null;
    mimeType?: string | null;
    caption?: string | null;
    aiSummary?: string | null;
    transcriptText?: string | null;
    extractedText?: string | null;
  } | null;
};

type ThreadRequestRecord = {
  id: string;
  status?: string | null;
  requestType?: string | null;
  title?: string | null;
  summary?: string | null;
  routeFrom?: string | null;
  routeTo?: string | null;
  travelDateText?: string | null;
  travelDate?: string | null;
  passengerCount?: number | null;
  missingFields?: string[] | null;
  extractedEntities?: any;
  sourceMessageId?: string | null;
  latestMessageId?: string | null;
  metadata?: any;
  createdAt?: string | null;
  updatedAt?: string | null;
  EvidenceLinks?: RequestEvidenceLink[] | null;
};

const normalizeRequestStatus = (status?: string | null): ThreadRequestItem['status'] => {
  const key = String(status || '').toUpperCase();
  if (key === 'READY_FOR_PRICING' || key === 'READY_FOR_QUOTE') return 'READY_FOR_PRICING';
  if (key === 'NEEDS_CLARIFICATION') return 'NEEDS_CLARIFICATION';
  if (key === 'OPEN') return 'NEEDS_CLARIFICATION';
  if (key === 'WAITING_CLIENT') return 'WAITING_CLIENT';
  if (key === 'CONVERTED_TO_CHARTER' || key === 'CONVERTED_TO_SCHEDULED' || key === 'COMPLETED' || key === 'CANCELLED') return 'DONE';
  return 'NEEDS_REPLY';
};

const mapEvidenceLinks = (links?: RequestEvidenceLink[] | null): ThreadRequestEvidence[] | undefined => {
  if (!Array.isArray(links) || links.length === 0) return undefined;
  const mapped = links
    .map(link => {
      const asset = link.MediaAsset;
      if (!asset) return null;
      return {
        id: asset.id,
        evidenceType: String(asset.evidenceType || 'UNKNOWN'),
        fileName: asset.fileName || null,
        mimeType: asset.mimeType || null,
        caption: asset.caption || null,
        summary: asset.aiSummary || asset.transcriptText || asset.extractedText || null,
      };
    })
    .filter(Boolean) as ThreadRequestEvidence[];
  return mapped.length ? mapped : undefined;
};

export const buildThreadPlanFromRequests = (requests: ThreadRequestRecord[], timelineEvents: TimelineEvent[]): ThreadPlan => {
  const openRequests = (Array.isArray(requests) ? requests : [])
    .filter(request => String(request?.status || '').toUpperCase() !== 'CANCELLED')
    .map(request => {
      const missingFields = Array.isArray(request.missingFields)
        ? request.missingFields
        : Array.isArray(request?.metadata?.missingFields)
          ? request.metadata.missingFields
          : [];
      const lastClientText =
        String(request?.metadata?.lastSourceText || request?.summary || request?.title || '').trim() ||
        'Client request';
      const sourceAt = String(request?.createdAt || request?.updatedAt || new Date().toISOString());
      return {
        id: request.id,
        sourceMessageId: request.sourceMessageId || request.latestMessageId || undefined,
        sourceAt,
        summary: String(request.summary || request.title || lastClientText),
        fromLabel: request.routeFrom || null,
        fromCode: request.routeFrom || null,
        toLabel: request.routeTo || null,
        toCode: request.routeTo || null,
        passengerCount: Number(request.passengerCount || 0) || null,
        dateLabel: request.travelDateText || null,
        isoDate: request.travelDate ? String(request.travelDate).slice(0, 10) : null,
        status: normalizeRequestStatus(request.status),
        missingFields: missingFields.map(value => String(value || '').trim()).filter(Boolean),
        lastClientText,
        evidence: mapEvidenceLinks(request.EvidenceLinks || undefined),
      } as ThreadRequestItem;
    })
    .sort((left, right) => new Date(right.sourceAt).getTime() - new Date(left.sourceAt).getTime());

  const latestInbound = getLastCustomerMessage(timelineEvents);
  const latestClientNeed = latestInbound ? getEventReadableText(latestInbound) : null;
  const summary = openRequests.length > 0
    ? `${openRequests.length} open request${openRequests.length === 1 ? '' : 's'} in this thread. Latest client need: ${latestClientNeed || openRequests[0]?.summary || 'Not available'}.`
    : (latestClientNeed ? `Client latest message: ${latestClientNeed}` : 'No open client requests detected in this thread.');
  const nextSteps = openRequests.length > 0
    ? openRequests.slice(0, 3).map(request => {
        if (request.status === 'NEEDS_CLARIFICATION') {
          return `Clarify ${request.missingFields.join(' and ')} for ${request.fromLabel || 'this route'}${request.toLabel ? ` -> ${request.toLabel}` : ''}.`;
        }
        if (request.status === 'NEEDS_REPLY') {
          return `Reply on ${request.fromLabel || 'the request'}${request.toLabel ? ` -> ${request.toLabel}` : ''} and move it to quote or waiting client.`;
        }
        if (request.status === 'WAITING_CLIENT') {
          return `Waiting on client confirmation for ${request.summary}.`;
        }
        return `Prepare pricing/quote for ${request.summary}.`;
      })
    : ['Read the latest client message and decide the next action.'];
  const handoverSummary = openRequests.length > 0
    ? `Thread handover: ${openRequests.length} active request${openRequests.length === 1 ? '' : 's'}. ${nextSteps[0]}`
    : 'Thread handover: no structured travel requests detected yet.';

  return {
    summary,
    handoverSummary,
    openRequests,
    latestClientNeed,
    nextSteps,
  };
};

const isClarifyingQuestionDraft = (draft: string): boolean =>
  /^(when|which date|what date|what day|how many|how many pax|how many passengers|one way or return|return or one way|what time|what time would you like|which route|from where|to where)\??$/i.test(draft);

const buildClarifyingQuestionSuggestion = (draft: string, lastInboundBody: string): string | null => {
  const routeHint = extractRouteHint(lastInboundBody);
  if (/^when\??$/i.test(draft)) {
    return routeHint ? `What date would you like to travel from ${routeHint}?` : 'What date would you like to travel?';
  }
  if (/^(how many|how many pax|how many passengers)\??$/i.test(draft)) {
    return 'How many passengers will be travelling?';
  }
  if (/^(one way or return|return or one way)\??$/i.test(draft)) {
    return 'Will this be one-way or return?';
  }
  if (/^(what time|what time would you like)\??$/i.test(draft)) {
    return 'What time would you like to depart?';
  }
  return null;
};

export const buildOperationalSuggestion = (
  draft: string,
  timelineEvents: TimelineEvent[],
  travelContext?: TravelContext | null,
  priceHint?: TravelPriceHint,
  threadPlan?: ThreadPlan | null,
  replyTargetEvent?: TimelineEvent | null,
): string | null => {
  const cleanDraft = normalizeWhitespace(draft);
  if (!cleanDraft) return null;
  const lastInbound = getFocusedCustomerMessage(timelineEvents, replyTargetEvent);
  const lastInboundBody = getEventReadableText(lastInbound);
  const routeHint = travelContext?.routeText || extractRouteHint(lastInboundBody);
  const clientChasing = /\b(still waiting|waiting|since|follow up|follow-up|really\??|hello\??|any update|update\??)\b/i.test(lastInbound?.body || '');
  const friendlyGreeting = /\b(hope all is well|how are you|how is your day|good morning|good afternoon|good evening|hello|hi there)\b/i.test(lastInboundBody);

  if (replyTargetEvent && friendlyGreeting) {
    return 'We are well, thank you. How may we assist you further?';
  }

  if (replyTargetEvent && !FLIGHT_INTENT_PATTERN.test(lastInboundBody) && !clientChasing) {
    return null;
  }

  if (isClarifyingQuestionDraft(cleanDraft)) {
    return buildClarifyingQuestionSuggestion(cleanDraft, lastInboundBody);
  }

  if (!lastInboundBody) return null;

  const firstOpenRequest = threadPlan?.openRequests[0];

  if (firstOpenRequest?.status === 'NEEDS_CLARIFICATION') {
    const routeLine = firstOpenRequest.fromLabel && firstOpenRequest.toLabel
      ? ` from ${firstOpenRequest.fromLabel} to ${firstOpenRequest.toLabel}`
      : '';
    if (firstOpenRequest.missingFields.includes('travel date') && firstOpenRequest.missingFields.includes('passenger count')) {
      return `Apologies for the delay. What date would you like to travel${routeLine}, and how many passengers will be travelling?`;
    }
    if (firstOpenRequest.missingFields.includes('travel date')) {
      return `Apologies for the delay. What date would you like to travel${routeLine}?`;
    }
    if (firstOpenRequest.missingFields.includes('passenger count')) {
      return `Apologies for the delay. How many passengers will be travelling${routeLine}?`;
    }
  }

  if (travelContext?.isFlightRequest && clientChasing && travelContext.hasEnoughDetailsForPricing && priceHint?.seatPriceUSD) {
    const priceLine = `${priceHint.currencyCode} ${priceHint.seatPriceUSD.toFixed(2)} per seat`;
    const totalLine = priceHint.totalPriceUSD ? `, about ${priceHint.currencyCode} ${priceHint.totalPriceUSD.toFixed(2)} total` : '';
    return `Apologies for the delay. For ${travelContext.passengerCount} passengers from ${travelContext.fromLabel} to ${travelContext.toLabel}${travelContext.dateLabel ? ` on ${travelContext.dateLabel}` : ''}, the current guide price is ${priceLine}${totalLine}. Would you like me to prepare the quote?`;
  }

  if (travelContext?.isFlightRequest && travelContext.hasCompleteRoute && !travelContext.dateLabel && travelContext.passengerCount) {
    return `Thank you. Please confirm the travel date for ${travelContext.passengerCount} passengers from ${travelContext.fromLabel} to ${travelContext.toLabel}.`;
  }

  if (travelContext?.isFlightRequest && travelContext.hasCompleteRoute && travelContext.dateLabel && !travelContext.passengerCount) {
    return `Thank you. How many passengers will be travelling from ${travelContext.fromLabel} to ${travelContext.toLabel}${travelContext.dateLabel ? ` on ${travelContext.dateLabel}` : ''}?`;
  }

  if (/flight/i.test(lastInboundBody) && !/\b(date|day|passenger|pax|return|one-way|one way)\b/i.test(cleanDraft)) {
    return routeHint
      ? `Thank you. What date would you like to travel from ${routeHint}, and how many passengers will be travelling?`
      : 'Thank you. What date would you like to travel, and how many passengers will be travelling?';
  }

  return null;
};

const buildProfessionalRewrite = (
  draft: string,
  preferredPhrase: string | null,
  needsTimelineCommitment: boolean,
  forbiddenMatches: string[],
  options?: { skipGreeting?: boolean; skipThankYou?: boolean },
): string => {
  let nextDraft = draft.trim();
  if (!nextDraft) return '';

  if (preferredPhrase && forbiddenMatches.length > 0) {
    forbiddenMatches.forEach(match => {
      const escaped = match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      nextDraft = nextDraft.replace(new RegExp(escaped, 'ig'), preferredPhrase);
    });
  }

  if (/^\s*(ok|okay|sure|noted)\b/i.test(nextDraft)) {
    nextDraft = nextDraft.replace(/^\s*(ok|okay|sure|noted)\b[:, -]*/i, 'Certainly. ');
  }

  if (!options?.skipGreeting && !/^(good\s+(morning|afternoon|evening)|hello|hi|dear|certainly)/i.test(nextDraft)) {
    nextDraft = `Good day. ${nextDraft}`;
  }

  if (needsTimelineCommitment && !/\b(today|tomorrow|shortly|within|by\s+\w+|before|after|hour|minute|timeline|update)\b/i.test(nextDraft)) {
    nextDraft = `${nextDraft.replace(/[.!\s]*$/, '')}. We will revert with a clear update shortly.`;
  }

  if (!options?.skipThankYou && !/(thank you|kindly)/i.test(nextDraft)) {
    nextDraft = `Thank you for your message. ${nextDraft}`;
  }

  return normalizeWhitespace(nextDraft);
};

export const detectClientReferences = (timelineEvents: TimelineEvent[]): ClientReference[] => {
  const results: ClientReference[] = [];
  const seen = new Set<string>();
  const inboundEvents = timelineEvents.filter(event => event.kind === 'INBOUND');

  for (const event of inboundEvents) {
    const text = getEventReadableText(event);
    if (!text) continue;

    // Price references: "you gave me 1000 per pax", "price was 500", "quoted 2000"
    const pricePatterns = [
      /(?:gave\s+(?:me|you)|quoted\s+(?:me|you)|price\s+was|told\s+(?:me|us)|said\s+it\s+was|it\s+was|offer(?:ed)?\s+(?:me|us)?)\s+(?:usd\s*|kes\s*|\$|us\s*\$)?\s*(\d[\d,]*(?:\.\d{1,2})?)/gi,
      /(?:usd|kes|us\s*\$|\$)\s*(\d[\d,]*(?:\.\d{1,2})?)\s*(?:per\s+(?:pax|person|seat|head))?/gi,
      /(\d[\d,]*(?:\.\d{1,2})?)\s*(?:usd|kes)\b/gi,
    ];
    for (const pattern of pricePatterns) {
      for (const match of text.matchAll(pattern)) {
        const raw = match[1]?.replace(/,/g, '') || '';
        const num = parseFloat(raw);
        if (!raw || !Number.isFinite(num) || num < 10) continue;
        const key = `price-${raw}`;
        if (!seen.has(key)) {
          seen.add(key);
          results.push({
            id: key,
            type: 'PRICE',
            rawText: normalizeWhitespace(match[0]),
            value: raw,
            label: `Price reference: ${raw}`,
            messageId: event.messageId,
            messageAt: event.at,
          });
        }
      }
    }

    // Document references: invoice, quote, booking, estimate + number/code
    const docPattern = /\b(invoice|inv|quotation|quote|booking|reservation|estimate|est|ref(?:erence)?)\s*[:#]?\s*([a-z0-9]{4,})\b/gi;
    for (const match of text.matchAll(docPattern)) {
      const typeWord = match[1].toLowerCase();
      const value = match[2];
      const key = `doc-${typeWord.slice(0, 3)}-${value}`;
      if (!seen.has(key)) {
        seen.add(key);
        const type: ClientReference['type'] =
          typeWord.startsWith('inv') ? 'INVOICE' :
          typeWord.startsWith('quot') ? 'QUOTE' :
          typeWord.startsWith('book') || typeWord.startsWith('res') ? 'BOOKING' :
          typeWord.startsWith('est') ? 'ESTIMATE' : 'QUOTE';
        const typeLabel = type.charAt(0) + type.slice(1).toLowerCase();
        results.push({
          id: key,
          type,
          rawText: normalizeWhitespace(match[0]),
          value,
          label: `${typeLabel} #${value}`,
          messageId: event.messageId,
          messageAt: event.at,
        });
      }
    }
  }

  return results.slice(0, 8);
};

export const analyzeDraft = (
  draft: string,
  channelSettings: CommunicationAgentChannelSettings | null,
  timelineEvents: TimelineEvent[],
  selectedThreadLabel: string,
  replyTargetEvent?: TimelineEvent | null,
): DraftAnalysis => {
  const cleanDraft = normalizeWhitespace(draft);
  const references = extractConversationReferences(timelineEvents);
  const lastInbound = getFocusedCustomerMessage(timelineEvents, replyTargetEvent);
  const lastOutbound = getLastOutboundMessage(timelineEvents);
  const hostileOutbound = getRecentHostileOutbound(timelineEvents);
  const lastInboundText = getEventReadableText(lastInbound);
  const lastOutboundText = getEventReadableText(lastOutbound);
  const summary = lastInboundText
    ? `${replyTargetEvent ? 'Reply target' : 'Client last asked'}: ${lastInboundText.slice(0, 180)}`
    : lastOutboundText
      ? `Last reply sent: ${lastOutboundText.slice(0, 180)}`
      : `Open conversation with ${selectedThreadLabel}.`;

  const nextActions: string[] = [];
  const checklist: string[] = [];

  if (!channelSettings?.enabled || !channelSettings.draftGuardian.enabled) {
    return {
      status: cleanDraft ? 'OK' : 'IDLE',
      findings: [],
      suggestedRewrite: cleanDraft,
      checklist,
      references,
      summary,
      nextActions,
    };
  }

  const findings: DraftFinding[] = [];
  const preferredPhrases = channelSettings.draftGuardian.preferredPhrases || [];
  const forbiddenPhrases = channelSettings.draftGuardian.forbiddenPhrases || [];
  const forbiddenMatches = forbiddenPhrases.filter(phrase => phrase && cleanDraft.toLowerCase().includes(phrase.toLowerCase()));
  const preferredMatch = preferredPhrases.find(phrase => phrase && cleanDraft.toLowerCase().includes(phrase.toLowerCase())) || null;
  const timelineSensitive = /\b(when|timeline|today|tomorrow|friday|monday|tuesday|wednesday|thursday|saturday|sunday|urgent|asap|deadline)\b/i.test(lastInboundText);
  const hasCommitment = /\b(today|tomorrow|shortly|within|by\s+\w+|before|after|hour|minute|timeline|update)\b/i.test(cleanDraft);
  const hasApology = /\b(sorry|apologies|apologise|apologize)\b/i.test(cleanDraft);
  const travelIntent = /\b(flight|travel|trip|book|booking|from\s+.+\s+to\s+.+)\b/i.test(lastInboundText);
  const asksForDate = /\b(date|day)\b/i.test(cleanDraft);
  const asksForPassengers = /\b(passenger|passengers|pax|people)\b/i.test(cleanDraft);
  const clientChasing = /\b(still waiting|waiting|since|follow up|follow-up|really\??|hello\??|any update|update\??)\b/i.test(lastInboundText);
  const friendlyGreeting = /\b(hope all is well|how are you|how is your day|good morning|good afternoon|good evening|hello|hi there)\b/i.test(lastInboundText);
  const tooCasual = /\b(let me check|i'?ll get back|no worries|ok\b|okay\b|yeah\b|got it\b|cool\b)\b/i.test(cleanDraft);
  const greetingMissing = !/^(good\s+(morning|afternoon|evening)|hello|hi|dear|thank you|certainly)\b/i.test(cleanDraft);
  const hostileLanguage = SEVERE_HOSTILE_PATTERN.test(cleanDraft);
  const clarifyingQuestionDraft = isClarifyingQuestionDraft(cleanDraft);
  const clarifyingQuestionSuggestion = clarifyingQuestionDraft
    ? buildClarifyingQuestionSuggestion(cleanDraft, lastInboundText)
    : null;

  if (!cleanDraft) {
    checklist.push('Write a reply before sending.');
  } else {
    if (hostileOutbound && !hasApology) {
      findings.push({
        id: 'recovery-required',
        severity: 'HIGH',
        title: 'Conversation recovery required',
        detail: 'A recent outbound message was hostile. The next reply must reset tone and recover the conversation.',
        suggestion: travelIntent
          ? 'Apologies for the delay. Please share your preferred travel date and number of passengers so I can assist right away.'
          : 'Apologies for the earlier delay. Let me help you with this right away.',
      });
      nextActions.push('Reset the conversation with an apology before anything else.');
    }

    if (clientChasing && !hasApology) {
      findings.push({
        id: 'delay-acknowledgement-missing',
        severity: 'MEDIUM',
        title: 'Delay acknowledgement missing',
        detail: 'The client is following up or waiting. The reply should acknowledge the delay directly.',
        suggestion: 'Apologies for the delay.',
      });
      nextActions.push('Acknowledge the waiting time before asking for more details.');
    }

    if (friendlyGreeting && !travelIntent && !/\b(we are|i am|all is well|doing well|thank you|thanks|how may we assist|how can we help)\b/i.test(cleanDraft)) {
      findings.push({
        id: 'greeting-reply-incomplete',
        severity: 'LOW',
        title: 'Greeting reply can be clearer',
        detail: 'The client sent a conversational greeting. A short acknowledgement and offer to help is better than an unrelated operational reply.',
        suggestion: 'We are well, thank you. How may we assist you further?',
      });
    }

    if (travelIntent && (!asksForDate || !asksForPassengers) && !hostileLanguage) {
      findings.push({
        id: 'missing-travel-details',
        severity: 'MEDIUM',
        title: 'Missing required travel details',
        detail: 'Before pricing or confirming options, collect the travel date and passenger count.',
        suggestion: extractRouteHint(lastInboundText)
          ? `Apologies for the delay. What date would you like to travel from ${extractRouteHint(lastInboundText)}, and how many passengers will be travelling?`
          : 'Apologies for the delay. What date would you like to travel, and how many passengers will be travelling?',
      });
      nextActions.push('Ask for the missing travel date and passenger count.');
    }

    if (clarifyingQuestionDraft && clarifyingQuestionSuggestion) {
      findings.push({
        id: 'clarifying-question-too-short',
        severity: 'MEDIUM',
        title: 'Clarifying question is too vague',
        detail: 'The follow-up should be specific enough for the client to answer immediately.',
        suggestion: clarifyingQuestionSuggestion,
      });
      nextActions.push('Ask a specific follow-up question.');
    }

    if (forbiddenMatches.length > 0) {
      findings.push({
        id: 'forbidden-phrases',
        severity: 'HIGH',
        title: 'Forbidden phrasing detected',
        detail: `The draft uses blocked wording: ${forbiddenMatches.slice(0, 3).join(', ')}.`,
        suggestion: preferredMatch || preferredPhrases[0] || 'Kindly allow us a moment to review this and we will revert shortly.',
      });
      nextActions.push('Replace blocked wording before sending.');
    }

    if (hostileLanguage) {
      findings.push({
        id: 'hostile-language',
        severity: 'HIGH',
        title: 'Hostile language detected',
        detail: 'The draft includes abusive or dismissive language that must not be sent to a client.',
        suggestion: 'Thank you for your message. Kindly allow us a moment to review this and we will revert shortly.',
      });
      nextActions.push('Remove hostile language before sending.');
      checklist.push('Use respectful client-facing language at all times.');
    }

    if (!hostileLanguage && !clarifyingQuestionDraft && channelSettings.draftGuardian.requireProfessionalTone && tooCasual) {
      findings.push({
        id: 'tone-too-casual',
        severity: 'MEDIUM',
        title: 'Tone is too casual',
        detail: 'The draft reads informal for a premium client conversation.',
        suggestion: 'Kindly allow us a moment to review this and we will revert promptly.',
      });
      checklist.push('Use professional acknowledgement language.');
    }

    const hasHigherPriorityFinding = findings.some(finding => finding.severity === 'HIGH' || finding.severity === 'MEDIUM');

    if (!hostileLanguage && !friendlyGreeting && !clarifyingQuestionDraft && !hasHigherPriorityFinding && channelSettings.draftGuardian.requireProfessionalTone && greetingMissing) {
      findings.push({
        id: 'greeting-missing',
        severity: 'LOW',
        title: 'Professional framing missing',
        detail: 'Add a greeting or acknowledgement so the reply feels deliberate and premium.',
        suggestion: `Thank you for your message regarding ${selectedThreadLabel}.`,
      });
      checklist.push('Open with a greeting or acknowledgement.');
    }

    if (timelineSensitive && !hasCommitment) {
      findings.push({
        id: 'timeline-missing',
        severity: 'MEDIUM',
        title: 'Timeline commitment missing',
        detail: 'The client mentioned a deadline or timing expectation, but the draft does not acknowledge when you will revert.',
        suggestion: 'We will revert with a clear update shortly.',
      });
      nextActions.push('Acknowledge the requested timeline explicitly.');
    }

    if (!hostileLanguage && !friendlyGreeting && !clarifyingQuestionDraft && !hasHigherPriorityFinding && preferredPhrases.length > 0 && !preferredMatch) {
      findings.push({
        id: 'preferred-phrase-missing',
        severity: 'LOW',
        title: 'Preferred brand phrase not used',
        detail: 'The draft can be aligned more closely with your configured premium phrasing.',
        suggestion: preferredPhrases[0],
      });
      checklist.push('Use one of the approved brand phrases.');
    }
  }

  const blockSeverities = new Set(channelSettings.draftGuardian.blockSeverities || []);
  const shouldBlock = channelSettings.draftGuardian.mode === 'BLOCK' && findings.some(finding => blockSeverities.has(finding.severity));
  const status = !cleanDraft ? 'IDLE' : shouldBlock ? 'BLOCK' : findings.length > 0 ? 'WARN' : 'OK';
  const suggestedRewrite = cleanDraft
    ? clarifyingQuestionSuggestion || buildProfessionalRewrite(cleanDraft, preferredMatch || preferredPhrases[0] || null, timelineSensitive, forbiddenMatches, {
        skipGreeting: clarifyingQuestionDraft,
        skipThankYou: clarifyingQuestionDraft,
      })
    : '';

  if (!nextActions.length && cleanDraft && findings.length === 0) nextActions.push('Draft is ready to send.');

  return { status, findings, suggestedRewrite, checklist, references, summary, nextActions };
};
